import { api_fetch_throws } from "@/api/client";
import { copyToClipboard } from "@/utils/copy";
import { DateKind, DateRangeFilter, GlobalFilterState, CustomerFilter, Period, getStartEnd, DateKindToggle, IGlobalFilterState, analytics_date_kind, customer_source } from "@/global_filter";
import { Button } from "@/components/ui/button";
import { classes, useAsyncEffect, exactCurrencyFormatter, dateFormatter, Option, displayFormattedDistributor } from "@/utils";
import { Loader2, Trophy, FileText, CheckCircle2, ArrowUpRight, ChevronDown, ChevronUp, Share2 } from "lucide-react";
import { useEffect, useRef, useState } from "preact/hooks";
import { isSameDay } from "date-fns";
import { openDeductionDetailModal } from "@/deduction/deduction_detail_modal_state";
import { Bar, BarChart, CartesianGrid, LabelList, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

interface RecapListProps {
  isStandalone?: boolean;
  onClose?: () => void;
}

interface RepaymentSummary {
  id: string;
  original_deduction_id: string;
  amount: number;
  invoice_number: string;
  created_at: string;
  is_early_pay_win: boolean;
  category_name: string | null;
  category_description: string | null;
  deduction_source: string;
  dispute_type?: string | null;
}

interface DisputeSummary {
  id: string;
  deduction_id: string;
  dispute_type: string;
  amount: number;
  created_at: string;
  status: string;
  category_name: string | null;
  category_description: string | null;
  deduction_source: string;
}

interface UserTaskCompletion {
  user_name: string;
  user_email: string;
  tasks_completed: number;
}

interface TaskDetail {
  id: string;
  task_type: string;
  deduction_id: string | null;
  invoice_number: string | null;
  note: string | null;
  completed_at: string | null;
}

interface UserTaskDetails {
  user_name: string;
  user_email: string;
  tasks_completed: number;
  tasks: TaskDetail[];
}

interface AnalyticsMetrics {
  deductions_received: number;
  invoices_received: number;
  lines_extracted: number;
  categorizations: number;
  tasks_assigned: number;
  stages_changed: number;
  tasks_completed: number;
  backups_requested: number;
  disputes_filed: number;
  backups_received: number;
  disputes_won: number;
  deductions_validated: number;
  gl_mappings_made: number;
}

export interface RecapDisputeWinByType {
  type_key: string;
  type_label: string;
  disputed_amount: number;
  disputed_count: number;
  won_amount: number;
  won_count: number;
}

interface RecapData {
  wins: RepaymentSummary[];
  total_wins_amount: number;
  total_wins_count: number;
  disputes: DisputeSummary[];
  total_disputes_amount: number;
  total_disputes_count: number;
  backup_requests: number;
  backups_received: number;
  tasks: UserTaskCompletion[];
  task_details: UserTaskDetails[];
  total_tasks_completed: number;
  metrics: AnalyticsMetrics;
  total_repayments_since_start: number;
  pending_tasks_count: number;
  dispute_win_by_type: RecapDisputeWinByType[];
}

interface SummaryCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: typeof Trophy;
  colorClass: string;
}

function SummaryCard({ title, value, subtitle, icon: Icon, colorClass }: SummaryCardProps) {
  return (
    <div class={classes("rounded-lg border p-4", colorClass)}>
      <div class="flex items-center gap-3">
        <div class="p-2 rounded-full bg-white/50">
          <Icon class="h-5 w-5" />
        </div>
        <div>
          <p class="text-sm font-medium text-gray-600">{title}</p>
          <p class="text-2xl font-bold">{value}</p>
          {subtitle && <p class="text-xs text-gray-500">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
}

interface CollapsibleSectionProps {
  title: string;
  count: number;
  children: preact.ComponentChildren;
  defaultOpen?: boolean;
}

function CollapsibleSection({ title, count, children, defaultOpen = false }: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div class="border rounded-lg">
      <button
        class="w-full flex items-center justify-between p-4 hover:bg-gray-50"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span class="font-medium">{title} ({count})</span>
        {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {isOpen && <div class="border-t p-4">{children}</div>}
    </div>
  );
}

function formatTaskType(taskType: string): string {
  const typeMap: Record<string, string> = {
    'file-dispute': 'File Dispute',
    'pull-backup': 'Pull Backup',
    'followup': 'Follow Up',
    'validate': 'Validate',
    'map-accounting-code': 'Map Accounting Code',
    'expense': 'Expense',
    'update-dc-po': 'Update DC/PO',
  };
  return typeMap[taskType] || taskType;
}

export function has_recap_chart_data(rows: RecapDisputeWinByType[]): boolean {
  return rows.some((row: RecapDisputeWinByType) => row.disputed_amount > 0 || row.won_amount > 0);
}

export interface RecapDisputeWinRateRow extends RecapDisputeWinByType {
  /** Won dollars as a percentage of disputed dollars in this category; null when nothing was disputed. */
  win_rate_pct: number | null;
}

/** Adds each category's win rate: won ÷ disputed, by dollars. */
export function with_recap_win_rates(rows: RecapDisputeWinByType[]): RecapDisputeWinRateRow[] {
  return rows.map((row: RecapDisputeWinByType) => ({
    ...row,
    win_rate_pct: row.disputed_amount > 0 ? (Math.max(row.won_amount, 0) / row.disputed_amount) * 100 : null,
  }));
}

/** "42%", "<1%" for small non-zero rates, blank when there is no rate. */
export function format_recap_win_rate(pct: number | null): string {
  if (pct === null || !Number.isFinite(pct)) return "";
  if (pct > 0 && pct < 1) return "<1%";
  return `${Math.round(pct)}%`;
}

function formatAxisCurrency(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${Math.round(value / 1_000)}K`;
  return exactCurrencyFormatter(value);
}

interface RecapDisputeWinTooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: RecapDisputeWinRateRow;
  }>;
}

function RecapDisputeWinTooltip({ active, payload }: RecapDisputeWinTooltipProps) {
  if (!active || !payload?.length) return null;

  const row = payload[0].payload;
  return (
    <div class="rounded-lg border bg-white p-3 shadow-sm">
      <p class="font-semibold">{row.type_label}</p>
      <p class="text-sm text-gray-600">Filed: {exactCurrencyFormatter(row.disputed_amount)} ({row.disputed_count})</p>
      <p class="text-sm text-gray-600">Won: {exactCurrencyFormatter(row.won_amount)} ({row.won_count}){row.win_rate_pct !== null && <> · {format_recap_win_rate(row.win_rate_pct)} won</>}</p>
    </div>
  );
}

function RecapDisputeWinChart({ rows: raw_rows }: { rows: RecapDisputeWinByType[] }) {
  const hasData = has_recap_chart_data(raw_rows);
  const rows = with_recap_win_rates(raw_rows);
  const chartHeight = Math.max(260, rows.length * 54 + 92);

  return (
    <div class="rounded-lg border p-4 bg-white">
      <div class="flex items-center justify-between gap-3 mb-4">
        <div>
          <h3 class="font-medium">Disputes and Wins by Category</h3>
          <p class="text-sm text-gray-600">Chargebacks by category, with early pay shown separately. Filed counts disputes filed in this period; Won counts repayments received in this period, including on disputes filed earlier. Percentages are won ÷ filed within each category.</p>
        </div>
      </div>

      {!hasData ? (
        <div class="flex h-32 items-center justify-center rounded border border-dashed text-sm text-muted-foreground">
          No disputes or wins in this period
        </div>
      ) : (
        <div class="space-y-4">
          <div style={{ height: `${chartHeight}px` }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 72, left: 12, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tickFormatter={formatAxisCurrency} />
                <YAxis type="category" dataKey="type_label" width={96} />
                <Tooltip content={<RecapDisputeWinTooltip />} />
                <Legend />
                <Bar dataKey="disputed_amount" name="Filed" fill="#2563eb" radius={[0, 4, 4, 0]} />
                <Bar dataKey="won_amount" name="Won" fill="#16a34a" radius={[0, 4, 4, 0]}>
                  <LabelList dataKey="win_rate_pct" position="right" formatter={format_recap_win_rate} fill="#4b5563" fontSize={12} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div class="grid gap-2 md:grid-cols-3">
            {rows.map((row: RecapDisputeWinRateRow) => (
              <div key={row.type_key} class="rounded border bg-gray-50 p-3">
                <p class="font-medium">{row.type_label}</p>
                <div class="mt-2 space-y-1 text-sm text-gray-600">
                  <p>Filed: <span class="font-semibold text-gray-900">{exactCurrencyFormatter(row.disputed_amount)}</span> ({row.disputed_count})</p>
                  <p>Won: <span class="font-semibold text-gray-900">{exactCurrencyFormatter(row.won_amount)}</span> ({row.won_count}){row.win_rate_pct !== null && <> · {format_recap_win_rate(row.win_rate_pct)} won</>}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface UserTaskSectionProps {
  user: UserTaskDetails;
  onDeductionClick: (deductionId: string) => void;
}

function UserTaskSection({ user, onDeductionClick }: UserTaskSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div class="rounded-lg border bg-purple-50">
      <button
        class="w-full flex items-center justify-between p-3 hover:bg-purple-100"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div class="text-left">
          <p class="font-medium">{user.user_name}</p>
          <p class="text-sm text-gray-600">{user.user_email}</p>
        </div>
        <div class="flex items-center gap-2">
          <div class="text-right">
            <p class="text-xl font-bold text-purple-700">{user.tasks_completed}</p>
            <p class="text-xs text-gray-500">tasks</p>
          </div>
          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </button>
      {isExpanded && (
        <div class="border-t border-purple-200 p-3 space-y-2">
          {user.tasks.map((task: TaskDetail) => (
            <div
              key={task.id}
              class={classes(
                "flex items-center justify-between p-2 rounded bg-white",
                task.deduction_id ? "hover:bg-gray-50 cursor-pointer" : ""
              )}
              onClick={() => task.deduction_id && onDeductionClick(task.deduction_id)}
            >
              <div class="overflow-hidden">
                <p class="font-medium text-sm">{formatTaskType(task.task_type)}</p>
                {task.invoice_number && (
                  <p class="text-xs text-gray-600">Invoice: {task.invoice_number}</p>
                )}
                {task.note && (
                  <p class="text-xs text-gray-500 truncate">{task.note}</p>
                )}
              </div>
              <div class="flex items-center gap-2 flex-shrink-0">
                {task.completed_at && (
                  <p class="text-xs text-gray-500">{dateFormatter(task.completed_at)}</p>
                )}
                {task.deduction_id && <ArrowUpRight className="h-3 w-3 text-gray-400" />}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** True when the URL is an explicit shared recap link (`shared=1` from "Share recap"). */
export function has_shared_recap_date_range_in_url(search: string): boolean {
  const params = new URLSearchParams(search);
  return (
    params.get("shared") === "1" &&
    params.get("tab") === "recap" &&
    (params.has("start_date") || params.has("end_date"))
  );
}

/** Default recap filter: past 60 days on check date. */
export function get_recap_default_filter(): Pick<IGlobalFilterState, "startDate" | "endDate" | "dateKind"> {
  const { startDate, endDate } = getStartEnd("past60Days");
  return { startDate, endDate, dateKind: "check" };
}

/** Resolve recap defaults, honoring shared-link date_kind when present. */
export function resolve_recap_default_filter(search: string): Pick<IGlobalFilterState, "startDate" | "endDate" | "dateKind"> {
  const defaults = get_recap_default_filter();
  const params = new URLSearchParams(search);
  const shared_date_kind = params.get("date_kind");
  if (
    params.get("shared") === "1" &&
    params.get("tab") === "recap" &&
    (shared_date_kind === "invoice" ||
      shared_date_kind === "check" ||
      shared_date_kind === "posting" ||
      shared_date_kind === "deposit")
  ) {
    return { ...defaults, dateKind: shared_date_kind as DateKind };
  }
  return defaults;
}

function buildRecapShareUrl(startDate: Date | null, endDate: Date | null, distributor: string | null, dateKind: string | null): string {
  const params = new URLSearchParams();
  params.set("tab", "recap");
  params.set("shared", "1");
  if (startDate) params.set("start_date", startDate.toISOString());
  if (endDate) params.set("end_date", endDate.toISOString());
  if (distributor) params.set("source", customer_source(distributor));
  if (dateKind) params.set("date_kind", dateKind);
  return `${window.location.origin}/activities?${params.toString()}`;
}

interface TimeShortcutButtonProps {
  period: Period;
  label: string;
  currentStart: Option<Date>;
  currentEnd: Option<Date>;
}

function isDateRangeMatch(
  currentStart: Option<Date>,
  currentEnd: Option<Date>,
  periodStart: Option<Date>,
  periodEnd: Option<Date>
): boolean {
  if (currentStart === null && periodStart === null && currentEnd === null && periodEnd === null) {
    return true;
  }
  if (!currentStart || !currentEnd || !periodStart || !periodEnd) {
    return false;
  }
  return isSameDay(currentStart, periodStart) && isSameDay(currentEnd, periodEnd);
}

function TimeShortcutButton({ period, label, currentStart, currentEnd }: TimeShortcutButtonProps) {
  const { startDate: periodStart, endDate: periodEnd } = getStartEnd(period);
  const isActive = isDateRangeMatch(currentStart, currentEnd, periodStart, periodEnd);

  function handleClick() {
    GlobalFilterState.set(s => ({ ...s, startDate: periodStart, endDate: periodEnd }));
  }

  return (
    <Button
      variant={isActive ? "default" : "outline"}
      // @ts-ignore - Button size prop type mismatch
      size="sm"
      onClick={handleClick}
      class={classes(
        "text-xs px-2 py-1 h-7",
        isActive && "bg-blue-600 text-white hover:bg-blue-700"
      )}
    >
      {label}
    </Button>
  );
}

export function RecapList({ isStandalone, onClose }: RecapListProps) {
  const [data, setData] = useState<RecapData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Version counter: incremented on every fetch; used to discard stale responses
  const fetchVersion = useRef(0);
  const hasAppliedRecapDefault = useRef(false);

  const startDate = GlobalFilterState.use((s) => s.startDate);
  const endDate = GlobalFilterState.use((s) => s.endDate);
  const distributor = GlobalFilterState.use((s) => s.distributor);
  const dateKind = GlobalFilterState.use((s) => s.dateKind);

  useEffect(() => {
    if (hasAppliedRecapDefault.current) return;
    hasAppliedRecapDefault.current = true;

    if (has_shared_recap_date_range_in_url(window.location.search)) {
      return;
    }

    const defaults = resolve_recap_default_filter(window.location.search);
    GlobalFilterState.set((s) => ({ ...s, ...defaults }));
  }, []);

  async function handleShareRecap() {
    const url = buildRecapShareUrl(startDate, endDate, distributor, dateKind);
    await copyToClipboard(
      url,
      "Recap link copied — anyone who opens it will see the same recap",
      "Failed to copy link"
    );
  }

  useAsyncEffect(async () => {
    // Capture version at fetch start; discard response if a newer fetch has since started
    const version = ++fetchVersion.current;
    setIsLoading(true);
    setError(null);
    // Intentionally do NOT clear `data` here — keep previous results visible while loading
    try {
      const params: Record<string, string> = {};
      if (startDate) {
        params.start_date = startDate.toISOString();
      }
      if (endDate) {
        params.end_date = endDate.toISOString();
      }
      if (distributor) {
        params.source = customer_source(distributor);
      }
      if (dateKind) {
        params.date_kind = analytics_date_kind(dateKind);
      }

      const result = await api_fetch_throws<RecapData>("/activity/recap", { params });
      if (version === fetchVersion.current) {
        setData(result);
      }
    } catch (err: unknown) {
      if (version === fetchVersion.current) {
        setError(err instanceof Error ? err.message : "Failed to load recap data");
      }
    } finally {
      if (version === fetchVersion.current) {
        setIsLoading(false);
      }
    }
  }, [startDate, endDate, distributor, dateKind]);

  function handleDeductionClick(deductionId: string) {
    if (onClose) {
      onClose();
    }
    openDeductionDetailModal(deductionId);
  }

  // First load: no data yet — show full-page spinner
  if (isLoading && !data) {
    return (
      <div class="flex justify-center items-center h-[200px]">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div class="p-6 text-center text-red-600">
        {error}
      </div>
    );
  }

  if (!data) {
    return (
      <div class="p-6 text-center text-muted-foreground">
        No data available
      </div>
    );
  }

  return (
    <div class="flex flex-col h-full relative">
      {/* Subtle top-bar loading stripe while revalidating — data stays visible */}
      {isLoading && (
        <div class="absolute top-0 left-0 right-0 h-0.5 bg-blue-200 z-10 overflow-hidden">
          <div class="h-full bg-blue-500 animate-pulse w-1/3" />
        </div>
      )}
      {/* Filters */}
      <div class="flex flex-col gap-3 p-4 border-b">
        <div class="flex gap-4 flex-wrap items-center">
          <DateRangeFilter />
          <CustomerFilter />
          <DateKindToggle />
          {isStandalone && (
            <Button
              variant="outline"
              // @ts-ignore - Button size prop type mismatch
              size="sm"
              onClick={handleShareRecap}
              class="ml-auto"
            >
              <Share2 className="h-4 w-4 mr-2" />
              Share recap
            </Button>
          )}
        </div>
        <div class="flex gap-2 flex-wrap items-center">
          <span class="text-xs text-gray-500 mr-1">Quick filters:</span>
          <TimeShortcutButton period="currentMonth" label="This Month" currentStart={startDate} currentEnd={endDate} />
          <TimeShortcutButton period="previousMonth" label="Last Month" currentStart={startDate} currentEnd={endDate} />
          <TimeShortcutButton period="past30Days" label="30 Days" currentStart={startDate} currentEnd={endDate} />
          <TimeShortcutButton period="past60Days" label="60 Days" currentStart={startDate} currentEnd={endDate} />
          <TimeShortcutButton period="past90Days" label="90 Days" currentStart={startDate} currentEnd={endDate} />
          <TimeShortcutButton period="ytd" label="YTD" currentStart={startDate} currentEnd={endDate} />
          <TimeShortcutButton period="past6Months" label="6 Months" currentStart={startDate} currentEnd={endDate} />
          <TimeShortcutButton period="pastYear" label="1 Year" currentStart={startDate} currentEnd={endDate} />
        </div>
      </div>

      {/* Content */}
      <div class="overflow-auto max-h-[calc(100vh-200px)] p-4 space-y-6">
        {/* Summary Cards */}
        <div class="grid grid-cols-3 gap-4">
          <SummaryCard
            title="Wins"
            value={exactCurrencyFormatter(data.total_wins_amount)}
            subtitle={`${data.total_wins_count} repayments`}
            icon={Trophy}
            colorClass="bg-green-50 border-green-200"
          />
          <SummaryCard
            title="Disputes Filed"
            value={exactCurrencyFormatter(data.total_disputes_amount)}
            subtitle={`${data.total_disputes_count} disputes`}
            icon={FileText}
            colorClass="bg-blue-50 border-blue-200"
          />
          <SummaryCard
            title="Tasks Completed"
            value={data.total_tasks_completed}
            subtitle={`${data.tasks.length} team members`}
            icon={CheckCircle2}
            colorClass="bg-purple-50 border-purple-200"
          />
        </div>

        {/* Backup Stats */}
        <div class="grid grid-cols-2 gap-4">
          <div class="rounded-lg border p-4 bg-gray-50">
            <p class="text-sm text-gray-600">Backup Requests</p>
            <p class="text-xl font-bold">{data.backup_requests}</p>
          </div>
          <div class="rounded-lg border p-4 bg-gray-50">
            <p class="text-sm text-gray-600">Backups Received</p>
            <p class="text-xl font-bold">{data.backups_received}</p>
          </div>
        </div>

        <RecapDisputeWinChart rows={data.dispute_win_by_type} />

        {/* Wins List */}
        <CollapsibleSection title="Wins (Repayments)" count={data.wins.length} defaultOpen={data.wins.length > 0 && data.wins.length <= 10}>
          {data.wins.length === 0 ? (
            <p class="text-muted-foreground">No repayments in this period</p>
          ) : (
            <div class="space-y-2">
              {data.wins.map((win: RepaymentSummary) => (
                <div
                  key={win.id}
                  class="flex items-center justify-between p-3 rounded-lg bg-green-50 hover:bg-green-100 cursor-pointer"
                  onClick={() => handleDeductionClick(win.original_deduction_id)}
                >
                  <div>
                    <p class="font-medium">{exactCurrencyFormatter(win.amount)}</p>
                    <p class="text-sm text-gray-600">
                      Invoice: {win.invoice_number}
                      {win.category_name && ` • ${win.category_name}`}
                    </p>
                    <p class="text-xs text-gray-500">
                    {displayFormattedDistributor(win.deduction_source)} • {win.is_early_pay_win ? "Early Pay Win" : "Dispute Win"}: {dateFormatter(win.created_at)}
                    </p>
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-gray-400" />
                </div>
              ))}
            </div>
          )}
        </CollapsibleSection>

        {/* Disputes List */}
        <CollapsibleSection title="Disputes Filed" count={data.disputes.length} defaultOpen={data.disputes.length > 0 && data.disputes.length <= 10}>
          {data.disputes.length === 0 ? (
            <p class="text-muted-foreground">No disputes filed in this period</p>
          ) : (
            <div class="space-y-2">
              {data.disputes.map((dispute: DisputeSummary) => (
                <div
                  key={dispute.id}
                  class="flex items-center justify-between p-3 rounded-lg bg-blue-50 hover:bg-blue-100 cursor-pointer"
                  onClick={() => handleDeductionClick(dispute.deduction_id)}
                >
                  <div>
                    <p class="font-medium">{exactCurrencyFormatter(dispute.amount)}</p>
                    {(dispute.category_name || dispute.category_description) && (
                      <p class="text-sm text-gray-600">
                        {dispute.category_name}
                        {dispute.category_name && dispute.category_description && " • "}
                        {dispute.category_description}
                      </p>
                    )}
                    <p class="text-xs text-gray-500">
                    {displayFormattedDistributor(dispute.deduction_source)} • {dispute.dispute_type} • {dispute.status}: {dateFormatter(dispute.created_at)}
                    </p>
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-gray-400" />
                </div>
              ))}
            </div>
          )}
        </CollapsibleSection>

        {/* Tasks List */}
        <CollapsibleSection title="Task Completions" count={data.total_tasks_completed} defaultOpen={false}>
          {data.task_details.length === 0 ? (
            <p class="text-muted-foreground">No tasks completed in this period</p>
          ) : (
            <div class="space-y-4">
              {data.task_details.map((user: UserTaskDetails) => (
                <UserTaskSection key={user.user_email} user={user} onDeductionClick={handleDeductionClick} />
              ))}
            </div>
          )}
        </CollapsibleSection>

        {/* Additional Metrics */}
        <div class="rounded-lg border p-4 bg-gray-50">
          <h3 class="font-medium mb-3">Additional Metrics</h3>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p class="text-gray-600">Deductions Received</p>
              <p class="font-bold">{data.metrics.deductions_received}</p>
            </div>
            <div>
              <p class="text-gray-600">Invoices Received</p>
              <p class="font-bold">{data.metrics.invoices_received}</p>
            </div>
            <div>
              <p class="text-gray-600">Lines Extracted</p>
              <p class="font-bold">{data.metrics.lines_extracted}</p>
            </div>
            <div>
              <p class="text-gray-600">Categorizations</p>
              <p class="font-bold">{data.metrics.categorizations}</p>
            </div>
            <div>
              <p class="text-gray-600">Tasks Assigned</p>
              <p class="font-bold">{data.metrics.tasks_assigned}</p>
            </div>
            <div>
              <p class="text-gray-600">Stages Changed</p>
              <p class="font-bold">{data.metrics.stages_changed}</p>
            </div>
            <div>
              <p class="text-gray-600">Deductions Validated</p>
              <p class="font-bold">{data.metrics.deductions_validated}</p>
            </div>
            <div>
              <p class="text-gray-600">GL Mappings Made</p>
              <p class="font-bold">{data.metrics.gl_mappings_made}</p>
            </div>
            <div>
              <p class="text-gray-600">Total Repayments $ (All Time)</p>
              <p class="font-bold">{exactCurrencyFormatter(data.total_repayments_since_start)}</p>
            </div>
            <div>
              <p class="text-gray-600">Pending Tasks</p>
              <p class="font-bold">{data.pending_tasks_count}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
